import dynamic from "next/dynamic";
import { useDispatch } from "react-redux";
import {useRouter} from "next/router";
import { useEffect, useState } from "react";
import { ProductListWrapper } from "src/components/DesignSystem/Widgets2.0/ProductListWrapper";
import { specificThemeRender } from "src/utils/specificThemeRender";
import { getThemeComponent } from "src/utils/themeImports";
import { useSSRSelector } from "src/redux";
import { ProductCustomizationPopUp } from "src/components/ProductCustomizationPopUp";
import { IS_DESKTOP } from "src/utils/checkClientDeviceType";
import { getWishlistItemsByCustomerInfo } from "src/redux/actions/wishlist";
import { useSetWishlistState } from "src/hooks/useSetWishlistState";
import { ITEM_WISHLIST_STATE, WISHLIST_FEATURE_STATUS } from "src/constants/wishlist";
import { getRoute, PATH } from "src/utils/routes";
import { toggleGlobalAuthModal } from "src/redux/actions";
import { FullPageLoader } from "src/components/FullPageLoader/FullPageLoader";

const WishlistItemDeletionAlert = dynamic(() => import("./Components/DeleteWishlistItemConfirmationModal"));
const VariantSelectedListModal = dynamic(() => import("src/components/VariantSelectedListModal"));
const VariantChooseRepeatModal = dynamic(() => import("src/components/VariantChooseRepeatModal"));
const VariantModal = dynamic(() => import("src/components/VariantModal"));
const QuickViewModal = dynamic(() => import("src/components/DesignSystem/QuickViewModal"));
const PromosDrawerAndButton = dynamic(() => import("src/components/CouponsAndVouchers/PromosDrawerAndButton"));
const EmptyWishlistPage = dynamic(() => import("./Components/EmptyWishlistPage"));

const Wishlist = (props) => {
    const dispatch = useDispatch();

    const { storeData, personalizedProduct, userData, isLogin, wishlistData, wishlistFeatureActive, cartData } = useSSRSelector((state) => ({
        storeData: state.storeReducer.store,
        personalizedProduct: state.catalogReducer?.personalizedProduct,
        userData: state.userReducer?.data,
        isLogin: state.userReducer?.isLogin,
        wishlistData: state.wishlistReducer?.data,
        wishlistFeatureActive: state.storeReducer?.store?.services?.wishlist_flag,
        cartData: state.cartReducer,
    }));

    const [loading, setLoading] = useState(true);
    const [wishlistItemData, setWishlistItemData] = useState(wishlistData || []);
    const [firstRender, setFirstRender] = useState(true);
    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState({ 
      open: false,
      confirmCallback: (data: any) => {},
    });
    const {setWishlistState} = useSetWishlistState();
    const router = useRouter();

    useEffect(() => {
      if (wishlistFeatureActive !== WISHLIST_FEATURE_STATUS.WISHLIST_ACTIVE) {
        router.push(getRoute(PATH._404));
        return ;
      }
      if ( !isLogin ) {
        dispatch(toggleGlobalAuthModal(true));
        setLoading(false);
        return ;
      }
      setFirstRender(false);
      fetchWishlistData();
    }, [cartData]);

    const fetchWishlistData = () => {
      const payload = {
        store_id: storeData?.store_id,
        phone: userData?.phone,
      }
      dispatch(getWishlistItemsByCustomerInfo(payload, (data) => { 
        if (data) {
          changeWishlistItemStateToCart(data);
       } else {
          setWishlistItemData([])
          setLoading(false);
       }
      }));
    }

    const deleteWishlistCallback = (payload) => {
       setWishlistState(payload, (status: boolean) => {
        if (status) {
          fetchWishlistData();// wishlist item update after deleting a item from wishlist
        }
      }, {
        msg: 'Wishlist item deleted successfully'
      });
       setShowDeleteConfirmationModal({
      open: false,
      confirmCallback: (data: any) => {},
    })
    }

    const handleWishlistDeleteClick = (itemId: number) => {
      setShowDeleteConfirmationModal({
        open: true,
        confirmCallback: () => deleteWishlistCallback({
          item_id: itemId,
          status: ITEM_WISHLIST_STATE.REMOVE_FROM_WISHLIST,
        }),
      });   
    }

    const changeWishlistItemStateToCart = (data: any) => {
      const cartItemIds = cartData?.items?.map((item) => item.id || item.item_id);

      // Set wishlist items that are present in the cart
      data?.forEach((item) => {
        if (cartItemIds?.includes(item?.id)) {
          const payload = {
            item_id: item?.id,
            status: ITEM_WISHLIST_STATE.ADD_TO_CART
          };
            setWishlistState(
              payload,
              (status) => {},
              !firstRender ? { msg: 'Item added to cart from wishlist' } : undefined
            );
        }
      });
      // Filter out wishlist items that are not in the cart
      const wishlistNotInCart = data?.filter((item) => !cartItemIds.includes(item?.id));
      setWishlistItemData(wishlistNotInCart);
      setLoading(false);
    }

    return (
        <section className="wishlist-page">
            {specificThemeRender(storeData)?.components?.Header?.map((layout) => {
              return getThemeComponent(layout.sub_type, layout.name, layout);
            })}

            {loading ? (
              <div className="wishlist-loading">
                <FullPageLoader />
              </div>
            ) : 
            <div>
              {!wishlistItemData || wishlistItemData.length === 0 
              ? 
              <div className="tw-w-full tw-h-[80vh] tw-flex tw-justify-center tw-items-center"><EmptyWishlistPage /> </div>
              :
              <div className={`${IS_DESKTOP ? "tw-px-[57px] tw-py-[36px]" : "tw-px-[11px] tw-py-[15px]"}`}>
                  <span className="tw-font-inter tw-font-medium tw-text-[24px]">Wishlist</span>
                  <ProductListWrapper
                    customClass="tw-mt-[24px]"
                    productList={wishlistItemData}
                    oldCardConfigs={{
                      showButton: true,
                      noTagsReadState: 'fromLength',
                      isWishlistCard: true,
                      onWishlistDeleteClick: handleWishlistDeleteClick,
                    }}
                    calculateSelfDimensions
                  />
              </div>}
            </div>
            } 

            <VariantModal isWishlistProductCard isRedirect/>
            <VariantChooseRepeatModal />
            <VariantSelectedListModal />
            <QuickViewModal />
            {personalizedProduct?.personalizationPopUp && <ProductCustomizationPopUp />}
            <PromosDrawerAndButton />
            <WishlistItemDeletionAlert showDeleteConfirmationModal={showDeleteConfirmationModal} setShowDeleteConfirmationModal={setShowDeleteConfirmationModal}/>
        </section>
    )
}

export default Wishlist;